import { useEffect, useState } from 'react';
import { usePermissions, useSaveTenantBilling, useTenantBilling } from '../../hooks';
import type { BillingCycle, BillingTiming, ITenantBillingInput, ITenantSummary } from '../../types';
import { CYCLE_LABEL, TIMING_LABEL, isoToday, longDate } from '../Invoices/format';
import { nextPeriod } from '../../mocks/invoices';
import styles from './TenantBillingSettings.module.scss';

const empty = (tenant: ITenantSummary): ITenantBillingInput => ({
  cycle: 'quarterly',
  cycle_start: isoToday(),
  timing: 'advance',
  payment_terms_days: null,
  account_number: null,
  billing_name: tenant.display_name,
  billing_address: null,
  customer_address: null,
  po_number: null,
  vat_rate: null,
  notes: null,
});

const str = (value: string | number | null | undefined) =>
  value === null || value === undefined ? '' : String(value);

/** Billing cycle, account number and addresses for one tenant. Invoices are proposed from these. */
export const TenantBillingSettings = ({ tenant }: { tenant: ITenantSummary }) => {
  const { data: billing, isLoading } = useTenantBilling(tenant.name);
  const save = useSaveTenantBilling(tenant.name);
  const { canBill } = usePermissions();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ITenantBillingInput>(empty(tenant));

  useEffect(() => {
    if (billing) {
      const { tenant: _t, updated_at: _a, updated_by: _b, ...rest } = billing;
      setDraft(rest);
    }
  }, [billing]);

  const set = <K extends keyof ITenantBillingInput>(key: K, value: ITenantBillingInput[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  if (isLoading) return <p className={styles.muted}>Loading billing settings…</p>;

  const upcoming = billing ? nextPeriod(billing, null) : null;
  const error = save.error as { message?: string } | null;

  if (!editing) {
    return (
      <section className={styles.panel} aria-label="Billing settings">
        <header className={styles.head}>
          <h2 className={styles.title}>Billing settings</h2>
          {canBill ? (
            <button type="button" className={styles.ghost} onClick={() => setEditing(true)}>
              {billing ? 'Edit' : 'Set up billing'}
            </button>
          ) : null}
        </header>
        {billing ? (
          <dl className={styles.dl}>
            <dt>Cycle</dt>
            <dd>
              {CYCLE_LABEL[billing.cycle]}, {TIMING_LABEL[billing.timing].toLowerCase()}, from{' '}
              {longDate(billing.cycle_start)}
            </dd>
            <dt>Next period</dt>
            <dd>
              {upcoming
                ? `${longDate(upcoming.period_start)} to ${longDate(upcoming.period_end)}`
                : '—'}
            </dd>
            <dt>Payment terms</dt>
            <dd>
              {billing.payment_terms_days === null
                ? 'Company default'
                : `Net ${billing.payment_terms_days} days`}
            </dd>
            <dt>Account number</dt>
            <dd>{billing.account_number ?? <span className={styles.muted}>not set</span>}</dd>
            <dt>Invoice to</dt>
            <dd>
              {billing.billing_name ?? tenant.display_name}
              {billing.billing_address ? (
                <span className={styles.address}>{billing.billing_address}</span>
              ) : (
                <span className={styles.warn}> No billing address: add one before invoicing.</span>
              )}
            </dd>
            {billing.po_number ? (
              <>
                <dt>PO number</dt>
                <dd>{billing.po_number}</dd>
              </>
            ) : null}
            {billing.vat_rate !== null ? (
              <>
                <dt>VAT rate</dt>
                <dd>{billing.vat_rate}%</dd>
              </>
            ) : null}
          </dl>
        ) : (
          <p className={styles.muted}>
            No billing cycle set. Invoices default to quarterly from today until you set one.
          </p>
        )}
      </section>
    );
  }

  return (
    <section className={styles.panel} aria-label="Billing settings">
      <header className={styles.head}>
        <h2 className={styles.title}>Billing settings</h2>
      </header>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate(
            {
              ...draft,
              billing_name: draft.billing_name?.trim() || null,
              billing_address: draft.billing_address?.trim() || null,
              customer_address: draft.customer_address?.trim() || null,
              account_number: draft.account_number?.trim() || null,
              po_number: draft.po_number?.trim() || null,
              notes: draft.notes?.trim() || null,
            },
            { onSuccess: () => setEditing(false) },
          );
        }}
      >
        <div className={styles.row3}>
          <label className={styles.field}>
            <span>Cycle</span>
            <select
              value={draft.cycle}
              onChange={(event) => set('cycle', event.target.value as BillingCycle)}
            >
              {(Object.keys(CYCLE_LABEL) as BillingCycle[]).map((cycle) => (
                <option key={cycle} value={cycle}>
                  {CYCLE_LABEL[cycle]}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            <span>Cycle starts</span>
            <input
              type="date"
              value={draft.cycle_start}
              onChange={(event) => set('cycle_start', event.target.value)}
              required
            />
            <small>Periods are anchored on this date.</small>
          </label>
          <label className={styles.field}>
            <span>Billed</span>
            <select
              value={draft.timing}
              onChange={(event) => set('timing', event.target.value as BillingTiming)}
            >
              {(Object.keys(TIMING_LABEL) as BillingTiming[]).map((timing) => (
                <option key={timing} value={timing}>
                  {TIMING_LABEL[timing]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className={styles.row3}>
          <label className={styles.field}>
            <span>Account number</span>
            <input
              value={str(draft.account_number)}
              onChange={(event) => set('account_number', event.target.value)}
              placeholder="e.g. 44851"
            />
          </label>
          <label className={styles.field}>
            <span>Payment terms (days)</span>
            <input
              type="number"
              min={0}
              value={str(draft.payment_terms_days)}
              onChange={(event) =>
                set(
                  'payment_terms_days',
                  event.target.value === '' ? null : Number(event.target.value),
                )
              }
              placeholder="company default"
            />
          </label>
          <label className={styles.field}>
            <span>VAT rate override (%)</span>
            <input
              type="number"
              min={0}
              max={100}
              step="0.1"
              value={str(draft.vat_rate)}
              onChange={(event) =>
                set('vat_rate', event.target.value === '' ? null : Number(event.target.value))
              }
              placeholder="company default"
            />
          </label>
        </div>
        <label className={styles.field}>
          <span>Invoice to (legal name)</span>
          <input
            value={str(draft.billing_name)}
            onChange={(event) => set('billing_name', event.target.value)}
            placeholder={tenant.display_name}
          />
        </label>
        <div className={styles.row2}>
          <label className={styles.field}>
            <span>Billing address</span>
            <textarea
              rows={4}
              value={str(draft.billing_address)}
              onChange={(event) => set('billing_address', event.target.value)}
              placeholder={'The Brewery\nBlandford St. Mary\nDorset DT11 9LS'}
            />
          </label>
          <label className={styles.field}>
            <span>Customer address (if different)</span>
            <textarea
              rows={4}
              value={str(draft.customer_address)}
              onChange={(event) => set('customer_address', event.target.value)}
            />
          </label>
        </div>
        <div className={styles.row2}>
          <label className={styles.field}>
            <span>PO number</span>
            <input
              value={str(draft.po_number)}
              onChange={(event) => set('po_number', event.target.value)}
            />
          </label>
          <label className={styles.field}>
            <span>Notes</span>
            <input
              value={str(draft.notes)}
              onChange={(event) => set('notes', event.target.value)}
              placeholder="anything finance should know"
            />
          </label>
        </div>
        {error ? (
          <p className={styles.error} role="alert">
            {error.message ?? 'Could not save'}
          </p>
        ) : null}
        <div className={styles.actions}>
          <button type="button" className={styles.ghost} onClick={() => setEditing(false)}>
            Cancel
          </button>
          <button type="submit" className={styles.primary} disabled={save.isLoading}>
            {save.isLoading ? 'Saving…' : 'Save billing settings'}
          </button>
        </div>
      </form>
    </section>
  );
};
